/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { frame } from '../flight/state.ts'
import { SPILL_SEGMENTS } from './tubesData.ts'
import { glsl, GROUND, SURFACE2, CYAN, CYAN_WASH, MAGENTA, YELLOW } from '../palette.ts'

// FOUNDATION §6.1 — one plane recentred on the camera every frame; the grid is drawn in
// the fragment shader in WORLD coordinates, so recentring is invisible and the floor is
// literally endless. Dormant grid ignites into cyan traces at ORGANIZE; light tubes
// spill --cyan-wash onto it (FOUNDATION §4.2 mechanism 1 — "the effect").

const VERT = /* glsl */ `
varying vec3 vW;
void main() {
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vW = wp.xyz;
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`

const FRAG = /* glsl */ `
uniform float uTrace, uChroma, uFog, uTime;
uniform vec3 uBg;
uniform vec3 uCam;
uniform vec3 uSegA[16];
uniform vec3 uSegB[16];
varying vec3 vW;
float distSeg(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a, ba = b - a;
  float h = clamp(dot(pa, ba) / max(dot(ba, ba), 1e-5), 0.0, 1.0);
  return length(pa - ba * h);
}
float hashf(float n) { return fract(sin(n) * 43758.5453); }
void main() {
  vec2 w = vW.xz;
  vec3 col = ${glsl(GROUND)};

  // desk-pitch grid (3.2 × 2.4), dormant neutral
  vec2 q = w / vec2(3.2, 2.4);
  vec2 fr = fract(q);
  vec2 dc = min(fr, 1.0 - fr) * vec2(3.2, 2.4);
  float aa = fwidth(min(dc.x, dc.y)) * 1.6 + 0.008;
  float lineX = 1.0 - smoothstep(0.014, 0.014 + aa, dc.y); // lines of constant z
  float lineZ = 1.0 - smoothstep(0.014, 0.014 + aa, dc.x); // lines of constant x
  float line = max(lineX, lineZ);
  // the grid dies BEFORE the fog does — no ruled horizon, no visible world edge (law 2)
  float distG = length(vW - uCam);
  float foG = 1.0 - exp(-uFog * uFog * distG * distG);
  float gridVis = pow(1.0 - foG, 2.2);
  col = mix(col, ${glsl(SURFACE2)}, line * 0.28 * gridVis);

  // ORGANIZE: traces ignite, pulses travelling desk -> junction -> desk
  float rowSeed = hashf(floor(q.y) * 7.31);
  float colSeed = hashf(floor(q.x) * 3.77);
  float pulseX = exp(-pow((fract(w.x * 0.06 - uTime * 0.4 + rowSeed) - 0.5) * 7.0, 2.0));
  float pulseZ = exp(-pow((fract(w.y * 0.06 - uTime * 0.33 + colSeed) - 0.5) * 7.0, 2.0));
  // only some lines are routed (it's a routing grid, not wallpaper)
  float routedX = step(0.45, rowSeed);
  float routedZ = step(0.62, colSeed);
  // traces outlive the dormant grid into the distance — information keeps its colour.
  // A few routed lines carry the other randommonkey.io ink dots (magenta / yellow)
  vec3 traceCol = ${glsl(CYAN)};
  float rowInk = hashf(floor(q.y) * 13.9);
  float colInk = hashf(floor(q.x) * 17.3);
  float inkPick = lineX > lineZ ? rowInk : colInk;
  if (inkPick > 0.94) traceCol = ${glsl(YELLOW)};
  else if (inkPick > 0.86) traceCol = ${glsl(MAGENTA)};
  float traceGlow = lineX * routedX * (0.3 + 0.9 * pulseX) + lineZ * routedZ * (0.3 + 0.9 * pulseZ);
  col = mix(col, traceCol, clamp(traceGlow, 0.0, 1.0) * uTrace * uChroma * pow(1.0 - foG, 0.8));

  // light-tube spill — cyan wash on the white ground near the runs
  float sp = 0.0;
  for (int i = 0; i < 16; i++) {
    float d = distSeg(w, uSegA[i].xz, uSegB[i].xz);
    sp += exp(-d * d * 0.30);
  }
  sp = clamp(sp, 0.0, 1.0);
  col = mix(col, ${glsl(CYAN_WASH)}, sp * uChroma * 0.9);

  // aerial perspective into the background — the horizon must never exist
  float dist = length(vW - uCam);
  float fo = 1.0 - exp(-uFog * uFog * dist * dist);
  col = mix(col, uBg, fo);
  gl_FragColor = vec4(col, 1.0);
}
`

export function Floor() {
  const meshRef = useRef<THREE.Mesh>(null)
  const material = useMemo(
    () =>
      new THREE.ShaderMaterial({
        vertexShader: VERT,
        fragmentShader: FRAG,
        uniforms: {
          uTrace: { value: 0 },
          uChroma: { value: 0 },
          uFog: { value: 0.016 },
          uTime: { value: 0 },
          uBg: { value: new THREE.Color() },
          uCam: { value: new THREE.Vector3() },
          uSegA: { value: SPILL_SEGMENTS.map((s) => s.a) },
          uSegB: { value: SPILL_SEGMENTS.map((s) => s.b) },
        },
      }),
    [],
  )

  useFrame((st) => {
    const b = frame.beats
    const u = material.uniforms
    u.uTrace.value = b.trace
    u.uChroma.value = b.chroma
    u.uFog.value = b.fog
    u.uTime.value = st.clock.elapsedTime
    ;(u.uBg.value as THREE.Color).copy(frame.bg)
    ;(u.uCam.value as THREE.Vector3).copy(st.camera.position)
    if (meshRef.current) {
      meshRef.current.position.set(st.camera.position.x, 0, st.camera.position.z)
    }
  })

  return (
    <mesh ref={meshRef} rotation-x={-Math.PI / 2} material={material} frustumCulled={false}>
      <planeGeometry args={[600, 600]} />
    </mesh>
  )
}
